/**
 * Valley enhancement — Hessian vesselness
 *
 * A palm line is a dark *valley*, not a step edge. A plain gradient detector
 * finds skin texture, shadow borders and the outline of the hand just as
 * strongly as a rekhā. A Hessian-based (Frangi) filter at several scales
 * responds to elongated dark valleys specifically, which is the actual shape of
 * a rekhā — and it runs *before* edge detection for that reason. This is the
 * load-bearing choice in the palm pipeline.
 *
 * ── The filter ──────────────────────────────────────────────────────────────
 *
 * At each scale σ the scale-normalised Hessian (σ² · second Gaussian
 * derivatives) has eigenvalues λ1, λ2 with |λ1| ≤ |λ2|. For a dark line on a
 * lighter ground, λ2 is large and *positive* and λ1 is near zero:
 *
 *   V = exp(−R²/2β²) · (1 − exp(−S²/2c²)) · exp(−Q²/2γ²)     when λ2 > 0
 *
 *   R = |λ1| / |λ2|            blobness — near 1 for a spot, near 0 for a line
 *   S = √(λ1² + λ2²)           structure — suppresses noise
 *   Q = σ|∇I| / |λ2|           edgeness  — 0 at a valley's floor, ≈1 on a step (λ2 is σ²-normalised, so this is |∇I| / (σ · |I″|))
 *
 * ⚠ **The third factor is not in Frangi's original and is why this passes its
 * test.** With only the first two, a step edge produces a strong "ridge" response
 * on its dark side (the positive lobe of the second derivative) that is nearly as
 * large as a real valley's. But at the floor of a valley the gradient is zero,
 * whereas on the lobe of a step it is at its maximum — so weighting by how flat
 * the intensity is *at the point* separates them cleanly. A synthetic valley
 * scoring above a step of equal contrast is an acceptance test of this phase.
 *
 * ◆ `c` is an absolute contrast scale (gray in [0,1]), not "half the maximum
 * response". Normalising by the image's own maximum makes every image's
 * strongest structure look equally strong — which would make the valley/edge
 * comparison meaningless and a blank image look full of lines.
 *
 * © Manish Jagdish Thatte
 */

import { convolveSep, gaussKernel, makeGray } from './image.js';

export const DEFAULT_SCALES = Object.freeze([1.5, 2.0, 2.8, 4.0]);
export const DEFAULT_BETA = 0.5;
export const DEFAULT_C = 0.05;
export const DEFAULT_GAMMA = 0.35;

/**
 * Scale-normalised Hessian components at scale `sigma`.
 * Returns `{ xx, xy, yy, gx, gy }` as gray images.
 */
export function hessian(gray, sigma) {
  const g0 = gaussKernel(sigma, 0);
  const g1 = gaussKernel(sigma, 1);
  const g2 = gaussKernel(sigma, 2);
  const s2 = sigma * sigma;
  const scale = (img, f) => { for (let i = 0; i < img.data.length; i++) img.data[i] *= f; return img; };
  return {
    xx: scale(convolveSep(gray, g2, g0), s2),
    yy: scale(convolveSep(gray, g0, g2), s2),
    xy: scale(convolveSep(gray, g1, g1), s2),
    gx: convolveSep(gray, g1, g0),
    gy: convolveSep(gray, g0, g1)
  };
}

/**
 * Eigenvalues of the symmetric matrix [[xx, xy], [xy, yy]], ordered by absolute
 * value: `[λ1, λ2]` with |λ1| ≤ |λ2|.
 */
export function eigenvalues2x2(xx, xy, yy) {
  const mean = (xx + yy) / 2;
  const d = Math.sqrt(((xx - yy) / 2) ** 2 + xy * xy);
  const a = mean + d; const b = mean - d;
  return Math.abs(a) <= Math.abs(b) ? [a, b] : [b, a];
}

/** The vesselness of a single scale. */
export function vesselnessAtScale(gray, sigma, { beta = DEFAULT_BETA, c = DEFAULT_C, gamma = DEFAULT_GAMMA } = {}) {
  const { xx, xy, yy, gx, gy } = hessian(gray, sigma);
  const out = makeGray(gray.width, gray.height);
  const denomR = 2 * beta * beta; const denomS = 2 * c * c; const denomQ = 2 * gamma * gamma;
  for (let i = 0; i < out.data.length; i++) {
    const [l1, l2] = eigenvalues2x2(xx.data[i], xy.data[i], yy.data[i]);
    if (l2 <= 0) continue;                                  // dark valleys only
    const R = Math.abs(l1) / l2;
    const S2 = l1 * l1 + l2 * l2;
    const grad = Math.hypot(gx.data[i], gy.data[i]);
    const Q = (grad * sigma) / l2;                          // |∇I| / (σ·|I″|), unit-free
    out.data[i] = Math.exp(-(R * R) / denomR) * (1 - Math.exp(-S2 / denomS)) * Math.exp(-(Q * Q) / denomQ);
  }
  return out;
}

/**
 * Multi-scale vesselness: per-pixel maximum over `scales`.
 *
 * Returns `{ width, height, data, scaleIndex, scales }`; `scaleIndex[i]` is the
 * index of the scale that won at pixel i, kept because "which width of line
 * was this" is itself a measurement (deep, broad rekhās versus fine ones).
 */
export function vesselness(gray, scales = DEFAULT_SCALES, beta = DEFAULT_BETA, c = DEFAULT_C, gamma = DEFAULT_GAMMA) {
  const out = makeGray(gray.width, gray.height);
  const scaleIndex = new Uint8Array(out.data.length);
  scales.forEach((sigma, si) => {
    const v = vesselnessAtScale(gray, sigma, { beta, c, gamma });
    for (let i = 0; i < v.data.length; i++) {
      if (v.data[i] > out.data[i]) { out.data[i] = v.data[i]; scaleIndex[i] = si; }
    }
  });
  return { ...out, scaleIndex, scales: [...scales] };
}
